// src/pages/ServicesPage.js
import React from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  useColorModeValue,
  Icon,
  Flex,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Importing a wider range of icons
import { BsBuilding, BsBrush, BsGear, BsHouseDoor, BsClipboardData, BsShieldCheck } from 'react-icons/bs';

// A larger, more detailed list of services
const allServices = [
  {
    icon: BsBuilding,
    title: 'Commercial Construction',
    text: 'We deliver high-quality commercial structures, from office buildings and retail centers to hospitality venues, focusing on functionality and modern aesthetics that reflect your brand identity.',
  },
  {
    icon: BsBrush,
    title: 'Renovations & Remodeling',
    text: 'Our team specializes in transforming existing spaces. We handle everything from interior office makeovers to large-scale building retrofits, enhancing value and usability.',
  },
  {
    icon: BsGear,
    title: 'Industrial Construction',
    text: 'We construct robust industrial facilities, including warehouses, manufacturing plants, and distribution centers designed for optimal workflow, durability, and technological integration.',
  },
  {
    icon: BsHouseDoor,
    title: 'Residential Construction',
    text: 'From custom single-family homes to multi-unit residential complexes, we build living spaces with a focus on quality craftsmanship, comfort, and lasting value for homeowners.',
  },
  {
    icon: BsClipboardData,
    title: 'Pre-Construction & Planning',
    text: 'Our expert planning services ensure your project is set up for success from day one. We handle site analysis, budgeting, scheduling, and permitting to create a seamless roadmap.',
  },

  {
    icon: BsShieldCheck,
    title: 'Safety Management',
    text: 'Safety is at the core of our operations. We provide comprehensive safety management and compliance services to ensure a secure work environment on every project site.',
  },
];

// A dedicated card component for the services page grid
const ServiceGridCard = ({ icon, title, text }) => {
  return (
    <VStack
      bg={useColorModeValue('white', 'gray.700')}
      p={8}
      borderRadius="xl"
      boxShadow="lg"
      align="flex-start"
      spacing={4}
      transition="all 0.3s"
      _hover={{ transform: 'translateY(-5px)', boxShadow: 'xl' }}
    >
      <Flex
        align="center"
        justify="center"
        boxSize={16}
        borderRadius="lg"
        bg="orange.100"
      >
        <Icon as={icon} boxSize={8} color="orange.500" />
      </Flex>
      <Heading as="h3" size="md" fontWeight="bold">
        {title}
      </Heading>
      <Text color="gray.600" fontSize="md">
        {text}
      </Text>
    </VStack>
  );
};

export default function ServicesPage() {
  const bgColor = useColorModeValue('gray.50', 'gray.900');

  return (
    <>
      <Header />
      <Box as="main" bg={bgColor} py={{ base: 16, md: 24 }}>
        <VStack maxW="7xl" mx="auto" px={8} spacing={12}>
          {/* Page Header */}
          <VStack spacing={4} textAlign="center">
            <Breadcrumb>
              <BreadcrumbItem>
                <BreadcrumbLink as={RouterLink} to="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink href="#">Our Services</BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>
            <Heading as="h1" size="2xl" fontWeight="extrabold">
              Our Services
            </Heading>
            <Text maxW="3xl" color="gray.600">
              We provide a comprehensive range of construction services designed to meet the diverse needs of our clients, from initial planning to final handover.
            </Text>
          </VStack>

          {/* Services Grid */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={10} w="full">
            {allServices.map((service) => (
              <ServiceGridCard key={service.title} {...service} />
            ))}
          </SimpleGrid>
        </VStack>
      </Box>
      <Footer />
    </>
  );
}